import React, { useCallback, useEffect, useRef, useState } from "react";
import * as S from "./MobileHeroSimple.styles";
import { IHeroProps } from "./MobileHeroSimple.types";
import { absoluteAssetUrl, getOptimizedImageUrl } from "util/assets";
import { ImageSkeletonLoader } from "components/ImageSkeletonLoader";
import { useVideoCleanup } from "hooks/useDestroyVideoRef";

export const MobileHeroSimple = ({
  config,
  isInView,
}: Readonly<IHeroProps>) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const activeVideoRef = useRef<HTMLVideoElement>(null);
  useVideoCleanup(videoRef);
  useVideoCleanup(activeVideoRef);

  const [hasLoadedMainAsset, setHasLoadedMainAsset] = useState(false);
  const [isLoopActive, setIsLoopActive] = useState(false);

  const onVideoPlaybackEnd = useCallback(async () => {
    if (config.loopSrc && activeVideoRef?.current) {
      setIsLoopActive(true);
      try {
        activeVideoRef.current.currentTime = 0;
        await activeVideoRef.current.play();
      } catch (error) {
        console.error("Error playing loop video:", error);
      }
    } else if (videoRef.current) {
      try {
        videoRef.current.currentTime = 0;
        await videoRef.current.play();
      } catch (error) {
        console.error("Error playing main video:", error);
      }
    }
  }, [config.loopSrc]);

  const onLoadedMainVideo = useCallback(() => {
    setHasLoadedMainAsset(true);
  }, []);

  useEffect(() => {
    const intro = videoRef.current;
    const activeVideo = activeVideoRef.current;

    if (!isInView) {
      try {
        intro?.pause();
        activeVideo?.pause();
      } catch (error) {
        console.error("Error pausing video:", error);
      }
    } else {
      try {
        if (isLoopActive) {
          activeVideo?.play();
        } else {
          intro?.play();
        }
      } catch (error) {
        console.error("Error playing video:", error);
      }
    }
  }, [isInView, isLoopActive]);

  return (
    <S.HeroContainer>
      <S.Wrapper>
        {!!config?.showGradient && <S.Gradient />}

        <S.ActiveVideoWrapper>
          <ImageSkeletonLoader isVisible={!hasLoadedMainAsset} />

          <S.ActiveVideo
            ref={activeVideoRef}
            src={config?.loopSrc}
            muted
            loop
            $isActive={isLoopActive}
            playsInline
            preload="none"
          />

          <S.BackgroundVideo
            ref={videoRef}
            src={config?.src}
            muted
            playsInline
            autoPlay
            poster={getOptimizedImageUrl(
              absoluteAssetUrl(config?.poster),
              1920 * 2,
            )}
            $isVisible={!isLoopActive}
            preload="none"
            onLoadedData={onLoadedMainVideo}
            onEnded={onVideoPlaybackEnd}
          />
        </S.ActiveVideoWrapper>
      </S.Wrapper>
    </S.HeroContainer>
  );
};
